'use client';

import { featureCollection, type Point, point } from '@turf/helpers';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SOURCE_ID as LIVE_SOURCE_ID } from '@alltrails/maps/components/Waypoints/CustomRouteWaypoints';
import WaypointPinLayer from '@alltrails/maps/components/Waypoints/WaypointPinLayer';
import { customRouteWaypointsId } from '@alltrails/maps/utils/layers';
import type { PrintRouteWaypoint } from '../../types/PrintRouteWaypoint';

type PrintWaypointPinsProps = { waypoints: PrintRouteWaypoint[] };

/**
 * The route's labelled waypoints on the printed map (legacy: addMapWaypoints). A read-only twin of
 * CustomRouteWaypoints, not a reuse — the live one drags pins and eases the map to the selected
 * waypoint, which would reframe the preview under the user. Layer and source ids match the live
 * map's on purpose: they only need to be unique per map instance, and matching keeps the z-order.
 */
const PrintWaypointPins = ({ waypoints }: PrintWaypointPinsProps) => {
  const intl = useIntl();

  const waypointsData = useMemo(
    () =>
      featureCollection<Point>(
        waypoints.map(({ categoryUid, id, latitude, longitude, name, showTitle }, index) => {
          const title = showTitle ? name || intl.formatMessage({ defaultMessage: 'Waypoint {name}' }, { name: index + 1 }) : undefined;
          return point([longitude, latitude], { title, id, categoryUid });
        })
      ),
    [intl, waypoints]
  );

  if (waypointsData.features.length === 0) {
    return null;
  }

  return (
    <WaypointPinLayer layerId={customRouteWaypointsId} source={{ type: 'geojson', data: waypointsData }} sourceId={LIVE_SOURCE_ID} variant="user" />
  );
};

export default PrintWaypointPins;
